import {useQuery} from "@tanstack/react-query";
import axios from "axios";
import {useBrand} from "../../component/BrandProvider";
import {useParams} from 'react-router';
import { url } from "inspector";

type EmployeeSalesTodayData = {
    site: number;
    item: string;
    qty: number;
    current_timestamp: string;
}

type EmployeeSales = {
    site: number;
    item: string;
    qty: number;
}

// The five employee-sales endpoints below are cold TrueBlue/Firebird queries
// (14-43s measured on both brands). They set refetchInterval but no staleTime, so
// React Query treated every mount as stale and refetched cold; staleTime now matches
// the refetch cadence and gcTime outlives it, so a remount reuses the rows.
//
// The endpoint URL is part of the queryKey because these hooks resolve a per-brand
// base URL but previously cached under a brand-agnostic key. At staleTime 0 the
// immediate refetch hid that; caching without scoping the key would serve the other
// brand's numbers for up to five minutes after a switch.
//
// In-memory only: a full page reload still pays the cold query.
export const useGetEmployeeSalesLastTwoMTD = () => {
    const { brand } = useParams();
    const { url } = useBrand();

    const urlParam = brand === undefined ? undefined :
        brand === "cleanfreak"
        ? process.env.REACT_APP_API_URL_CLEANFREAK
        : process.env.REACT_APP_API_URL_RAINSTORM;

    const queryFn = async (): Promise<EmployeeSales[]> => {
        const response = await axios.get(`${urlParam || url}/api/ARM/Employee-Sales-Two-MTD`);
        return response.data;
    };
    

	return useQuery<EmployeeSales[]>({
		queryKey: ["Employee-Sales-Two-MTD", urlParam || url],
		queryFn: queryFn,
        refetchInterval: 60000 * 5,
        staleTime: 60000 * 5,
        gcTime: 60000 * 10
	});
};

export const useGetEmployeeSalesLastMTD = () => {
    const { brand } = useParams();
    const { url } = useBrand();

    const urlParam = brand === undefined ? undefined :
        brand === "cleanfreak"
        ? process.env.REACT_APP_API_URL_CLEANFREAK
        : process.env.REACT_APP_API_URL_RAINSTORM;

    const queryFn = async (): Promise<EmployeeSales[]> => {
        const response = await axios.get(`${urlParam || url}/api/ARM/Employee-Sales-Last-MTD`);
        return response.data;
    };
    

	return useQuery<EmployeeSales[]>({
		queryKey: ["Employee-Sales-Last-MTD", urlParam || url],
		queryFn: queryFn,
        refetchInterval: 60000 * 5,
        staleTime: 60000 * 5,
        gcTime: 60000 * 10
	});
};


export const useGetEmployeeSalesMTD = () => {
    const { brand } = useParams();
    const { url } = useBrand();

    const urlParam = brand === undefined ? undefined :
        brand === "cleanfreak"
        ? process.env.REACT_APP_API_URL_CLEANFREAK
        : process.env.REACT_APP_API_URL_RAINSTORM;

    const queryFn = async (): Promise<EmployeeSales[]> => {
        const response = await axios.get(`${urlParam || url}/api/ARM/Employee-Sales-MTD`);
        return response.data;
    };
    

	return useQuery<EmployeeSales[]>({
		queryKey: ["Employee-Sales-Data-MTD", urlParam || url],
		queryFn: queryFn,
        refetchInterval: 60000 * 5,
        staleTime: 60000 * 5,
        gcTime: 60000 * 10
	});
};


export const useGetEmployeeSpecificSalesMTD = () => {
    const { brand } = useParams();
    const { url } = useBrand();

    const urlParam = brand === undefined ? undefined :
        brand === "cleanfreak"
        ? process.env.REACT_APP_API_URL_CLEANFREAK
        : process.env.REACT_APP_API_URL_RAINSTORM;

    const queryFn = async (): Promise<EmployeeSales[]> => {
        const response = await axios.get(`${urlParam || url}/api/ARM/Employee-Specific-Sales-MTD`);
        return response.data;
    };
    

	return useQuery<EmployeeSales[]>({
		queryKey: ["Employee-Specific-Sales-MTD", urlParam || url],
		queryFn: queryFn,
        refetchInterval: 60000 * 5,
        staleTime: 60000 * 5,
        gcTime: 60000 * 10
	});
};


export const useGetEmployeeSalesToday = () => {
    const { brand } = useParams();
    const { url } = useBrand();

    const urlParam = brand === undefined ? undefined :
        brand === "cleanfreak"
        ? process.env.REACT_APP_API_URL_CLEANFREAK
        : process.env.REACT_APP_API_URL_RAINSTORM;

    const queryFn = async (): Promise<EmployeeSalesTodayData[]> => {
        const response = await axios.get(`${urlParam || url}/api/ARM/Employee-Sales-Today`);
        return response.data;
    };
    

	return useQuery<EmployeeSalesTodayData[]>({
		queryKey: ["Employee-Sales-Data-Today", urlParam || url],
		queryFn: queryFn,
        refetchInterval: 60000 * 5,
        staleTime: 60000 * 5,
        gcTime: 60000 * 10
	});
};

export const useGetLaborHoursToday = () => {
    const { brand } = useParams();
    const { url } = useBrand();

    const urlParam = brand === undefined ? undefined :
        brand === "cleanfreak"
        ? process.env.REACT_APP_API_URL_CLEANFREAK
        : process.env.REACT_APP_API_URL_RAINSTORM;

    const queryFn = async (): Promise<any[]> => {
        const response = await axios.get(`${urlParam || url}/api/Labor/Hours`);
        return response.data;
    };
    

	return useQuery<any[]>({
		queryKey: ["Site-Labor-Hours-Today"],
		queryFn: queryFn,
		refetchOnWindowFocus: false,
		staleTime: 60 * 60 * 1000,
		gcTime: 59 * 60 * 1000
    });
};

export const useGetLaborHoursWTD = () => {
    const { brand } = useParams();
    const { url } = useBrand();

    const urlParam = brand === undefined ? undefined :
        brand === "cleanfreak"
        ? process.env.REACT_APP_API_URL_CLEANFREAK
        : process.env.REACT_APP_API_URL_RAINSTORM;

    const queryFn = async (): Promise<any[]> => {
        const response = await axios.get(`${urlParam || url}/api/Labor/Hours-WTD`);
        return response.data;
    };
    

	return useQuery<any[]>({
		queryKey: ["Site-Labor-Hours-WTD"],
		queryFn: queryFn,
		refetchOnWindowFocus: false,
		staleTime: 60 * 60 * 1000,
		gcTime: 59 * 60 * 1000
    });
};

export const useGetLaborHoursPTD = () => {
    const { brand } = useParams();
    const { url } = useBrand();

    const urlParam = brand === undefined ? undefined :
        brand === "cleanfreak"
        ? process.env.REACT_APP_API_URL_CLEANFREAK
        : process.env.REACT_APP_API_URL_RAINSTORM;

    const queryFn = async (): Promise<any[]> => {
        const response = await axios.get(`${urlParam || url}/api/Labor/Hours-PTD`);
        return response.data;
    };
    

	return useQuery<any[]>({
		queryKey: ["Site-Labor-Hours-PTD"],
		queryFn: queryFn,
		refetchOnWindowFocus: false,
		staleTime: 60 * 60 * 1000,
		gcTime: 59 * 60 * 1000
    });
};

export const useGetLaborHoursFYTD = () => {
    const { brand } = useParams();
    const { url } = useBrand();

    const urlParam = brand === undefined ? undefined :
        brand === "cleanfreak"
        ? process.env.REACT_APP_API_URL_CLEANFREAK
        : process.env.REACT_APP_API_URL_RAINSTORM;

    const queryFn = async (): Promise<any[]> => {
        const response = await axios.get(`${urlParam || url}/api/Labor/Hours-FYTD`);
        return response.data;
    };
    

	return useQuery<any[]>({
		queryKey: ["Site-Labor-Hours-FYTD"],
		queryFn: queryFn,
		refetchOnWindowFocus: false,
		staleTime: 60 * 60 * 1000,
		gcTime: 59 * 60 * 1000
    });
};